import { mapValues, some, isEqual } from "lodash"
import { getToday } from "helpers/types/dates"
import { isDateRangeWithinNormalPerformancePricingRefreshWindow } from "connected-components/dashboard/helpers/date-calculations"

const INTRADAY_CONTRIBUTION_PREFIX_REGEX = /^intradayContribution(?:1d|1w|1m|Mtd)(.*)$/

export function getInstrumentSuffixForIntradayContributionPrefix(prefix = "") {
  const match = INTRADAY_CONTRIBUTION_PREFIX_REGEX.exec(prefix)
  return match ? match[1] : null
}

export function normalizeContributorKey(rowId) {
  return String(rowId || "").replace(/\./g, "_")
}

function addSuffixForModelProviderId(suffixesByModelProviderId, modelProviderId, suffix) {
  if (!suffixesByModelProviderId.has(modelProviderId)) {
    suffixesByModelProviderId.set(modelProviderId, new Set())
  }
  suffixesByModelProviderId.get(modelProviderId).add(suffix)
}

export function shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate(gatherer) {
  if (gatherer?.shouldAutoInvalidateOnPricingUpdates === false) {
    return false
  }
  return true
}

function hasNormalPerformanceGatherer(nonIntradayGatherers) {
  return nonIntradayGatherers.some((gatherer) =>
    typeof gatherer.prefix === "string" && gatherer.prefix.startsWith("performance"),
  )
}

function isExtendedDateRangeEndingToday(startDate, endDate) {
  return endDate === getToday()
    && Boolean(startDate)
    && !isDateRangeWithinNormalPerformancePricingRefreshWindow(startDate, endDate)
}

function getNormalPerformanceWarningSignature({ mainInstrumentId, startDate, endDate }) {
  return `${mainInstrumentId ?? ""}|${startDate}|${endDate}`
}

export function getNormalPerformanceAutoRefreshWarningState({
  nonIntradayGatherers,
  mainInstrumentId,
  startDate,
  endDate,
  lastShownSignature,
}) {
  if (!hasNormalPerformanceGatherer(nonIntradayGatherers)) {
    return { shouldShowWarning: false, nextShownSignature: lastShownSignature }
  }

  if (!isExtendedDateRangeEndingToday(startDate, endDate)) {
    return { shouldShowWarning: false, nextShownSignature: null }
  }

  const signature = getNormalPerformanceWarningSignature({ mainInstrumentId, startDate, endDate })
  return { shouldShowWarning: signature !== lastShownSignature, nextShownSignature: signature }
}

export function shouldSubscribeToIntradayPriceTicks({
  intradayPriceGatherers,
  intradayContributionQueryGatherers,
  intradayStandaloneGatherers = [],
}) {
  return intradayPriceGatherers.length > 0
    || intradayContributionQueryGatherers.length > 0
    || intradayStandaloneGatherers.length > 0
}

/**
 * Instrument prefix the freshness dot reads priceAsOf from, or null when no intraday
 * prices stream. With a price column it follows that gatherer (preferring the original
 * "intraday" instrument). Without one — a contribution-only intraday view, where prices
 * still stream to drive the contribution metrics — it falls back to "intraday": priceAsOf
 * is per-security and instrument-agnostic, so that prefix reads correctly for every leaf
 * and keeps the dot visible in intraday views that show no price column.
 *
 * The dot tracks the gatherer (original instrument), while prior-close row muting tracks
 * the visible price column (getVisibleIntradayPricePrefix). So when both the original and
 * a suffixed instrument stream but only the suffixed price column is shown, the dot reads
 * the original instrument and muting reads the suffixed one — intentional, not a bug.
 */
export function getActiveIntradayPricePrefix({ intradayPriceGatherers, hasActivePriceStream }) {
  if (!hasActivePriceStream) {
    return null
  }
  if (intradayPriceGatherers.length === 0) {
    return "intraday"
  }
  const priceGatherer = intradayPriceGatherers.find((gatherer) => gatherer.prefix === "intraday")
    || intradayPriceGatherers[0]
  return priceGatherer.prefix
}

/**
 * Builds an index from modelProviderId -> contribution metric suffixes present in the current tree.
 * Used to invalidate only the affected intraday contribution gatherers after price ticks.
 */
export function buildContributionSuffixIndexByModelProviderId(root) {
  const suffixesByModelProviderId = new Map()

  const nodesToVisit = [root]
  while (nodesToVisit.length > 0) {
    const currentNode = nodesToVisit.pop()
    if (!currentNode || typeof currentNode !== "object") {
      continue
    }

    for (const [prefix, contributionValue] of Object.entries(currentNode.values || {})) {
      const suffix = getInstrumentSuffixForIntradayContributionPrefix(prefix)
      const modelProviderId = contributionValue?.descriptors?.modelProviderId
      if (suffix == null || !modelProviderId) {
        continue
      }

      addSuffixForModelProviderId(suffixesByModelProviderId, modelProviderId, suffix)
      addSuffixForModelProviderId(suffixesByModelProviderId, normalizeContributorKey(modelProviderId), suffix)
    }

    if (!currentNode.children || typeof currentNode.children !== "object") {
      continue
    }
    nodesToVisit.push(...Object.values(currentNode.children))
  }

  return suffixesByModelProviderId
}

function mergeContributorNodeRecursive(mergedNode, sourceNode) {
  if (!mergedNode) {
    mergedNode = {
      label: sourceNode.label,
      values: {},
    }
  }

  for (const category in sourceNode.values) {
    mergedNode.values[category] = sourceNode.values[category]
  }

  // Sometimes a contributor shows up as notHeld in the composition query, but is held in the performance query
  // We want to show these securities as no longer held in the application
  // This is the case for securities that only have a pnl value, and no economic exposure
  if (mergedNode.notHeld && sourceNode.isNoLongerHeld) {
    mergedNode.isNoLongerHeld = sourceNode.isNoLongerHeld
  }

  if (sourceNode.children) {
    if (!mergedNode.children) {
      mergedNode.children = {}
    }

    for (const childId in sourceNode.children) {
      const child = sourceNode.children[childId]
      if (!mergedNode.children[childId]) {
        mergedNode.children[childId] = {
          id: child.id,
          isFactor: child.isFactor,
          label: child.label,
          notHeld: child.notHeld,
          isUnmodeled: child.isUnmodeled,
          isNoLongerHeld: child.isNoLongerHeld,
          values: {},
          pinnedRowOrder: child.pinnedRowOrder,
          tooltipValue: child.tooltipValue,
        }
      }
      mergeContributorNodeRecursive(mergedNode.children[childId], child)
    }
  }

  return mergedNode
}

export function mergeContributorTrees(contributorTrees) {
  return contributorTrees.reduce((mergedTree, sourceTree) => mergeContributorNodeRecursive(mergedTree, sourceTree), null)
}

function mergeFlatContributorsIntoRow(row, flatContributors, rowIdForKey) {
  if (!row) return row
  const lookupId = rowIdForKey ?? row?.id

  let values = row.values
  for (const { prefix, valuesByRowId } of flatContributors) {
    const next = lookupId == null ? undefined : valuesByRowId?.[lookupId]
    if (!next) continue
    const prev = values?.[prefix]
    const contributorDidChange = !isEqual(prev, next)
    if (!contributorDidChange) continue
    if (values === row.values) values = { ...(row.values || {}) }
    values[prefix] = next
  }

  let children = row.children
  if (row.children) {
    const newChildren = mapValues(row.children, (child, childKey) =>
      mergeFlatContributorsIntoRow(child, flatContributors, childKey),
    )
    if (some(newChildren, (child, key) => child !== row.children[key])) children = newChildren
  }

  if (values === row.values && children === row.children) return row
  return { ...row, values, children }
}

export function mergeFlatContributors(data, flatContributors) {
  if (!data || !flatContributors?.length) return data
  return mergeFlatContributorsIntoRow(data, flatContributors)
}
